import { MonDateInput } from "@/components/WeekStepper";
import { useDealStages } from "@/contexts/DealStagesContext";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import { useRoles } from "@/hooks/useRoles";
import {
  computeNumWeeks,
  computeProjectEndDate,
  formatUTCDateMDY,
  currentWeekStartISO,
} from "@/utils/dateUtils";
import { canClearAllAssignments, formatProjectDisplayName } from "@/utils/projectUtils";
import { ArrowDownAZ, Pencil } from "lucide-react";
import type { ReactNode } from "react";
import { ProjectColorPicker } from "./ProjectColorPicker";

/** A role id (uuid). */
type EmployeeRole = string;

function formatLikelihoodToClose(value: number): string {
  return `${value}%`;
}

export interface ProjectSummaryColumnFormData {
  name: string;
  office_id: string | null;
  deal_stage_id: string;
  likelihood_to_close: string;
  start_date: string;
  num_weeks: number | null;
  category_id: string;
}

export interface ProjectMetaHeaderRowProps {
  projectId: string;
  projectName: string;
  /** Stored custom color (`#rrggbb`) or null for the id-derived default. */
  projectColor: string | null | undefined;
  /** Live preview of a color being picked from the header swatch (no DB write). */
  onColorPreview: (hex: string) => void;
  /** Commit a color picked from the header swatch. */
  onColorCommit: (hex: string) => void;
  nameValue: string;
  editingName: boolean;
  onStartNameEdit: () => void;
  onNameChange: (value: string) => void;
  onNameSave: () => void | Promise<void>;
  onNameKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  nameSaving: boolean;
  onEditClick: () => void;
  expandCollapseButton?: ReactNode;
  dragHandle?: ReactNode;
  clientName?: string | null;
}

interface ProjectHeaderBarProps {
  headerRowProps: ProjectMetaHeaderRowProps;
  onAddRole?: (role: EmployeeRole) => void;
  addRoleDisabled?: boolean;
  onSortRoles?: () => void | Promise<void>;
  sortRolesDisabled?: boolean;
}

/**
 * Combined sticky-left header bar at the top of an expanded project.
 * Spans the meta column + role/employee column (600px) and contains the
 * drag handle, collapse button, edit button, project name, and Add Role select.
 */
export function ProjectHeaderBar({
  headerRowProps,
  onAddRole,
  addRoleDisabled,
  onSortRoles,
  sortRolesDisabled,
}: ProjectHeaderBarProps) {
  const { roles } = useRoles();
  const {
    projectId,
    projectName,
    projectColor,
    onColorPreview,
    onColorCommit,
    nameValue,
    editingName,
    onStartNameEdit,
    onNameChange,
    onNameSave,
    onNameKeyDown,
    nameSaving,
    onEditClick,
    expandCollapseButton,
    dragHandle,
    clientName,
  } = headerRowProps;
  const displayName = formatProjectDisplayName(projectName, clientName);
  return (
    <div className="project-header-bar">
      {dragHandle}
      {expandCollapseButton}
      <button
        className="icon-button"
        onClick={onEditClick}
        title="Edit project details"
        type="button"
        aria-label="Edit project"
      >
        <Pencil size={16} />
      </button>
      <ProjectColorPicker
        projectId={projectId}
        color={projectColor}
        onPreview={onColorPreview}
        onCommit={onColorCommit}
        title="Project color"
        ariaLabel={`Color for ${displayName}`}
      />
      {editingName ? (
        <input
          type="text"
          className="project-meta-cell__project-name-input"
          value={nameValue}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={() => onNameSave()}
          onKeyDown={onNameKeyDown}
          autoFocus
          disabled={nameSaving}
        />
      ) : (
        <span
          className="project-meta-cell__project-name project-card__editable"
          onClick={onStartNameEdit}
          title={displayName}
        >
          {displayName}
        </span>
      )}
      {onAddRole && (
        <select
          className="project-header-bar__add-role-select week-header-row__add-role-select"
          data-testid="add-role-select"
          value=""
          onChange={(e) => {
            const role = e.target.value as EmployeeRole;
            if (role) {
              onAddRole(role);
            }
          }}
          disabled={addRoleDisabled}
          aria-label="Add role"
        >
          <option value="" disabled>
            + Add role
          </option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.display_name}
            </option>
          ))}
        </select>
      )}
      {onSortRoles && (
        <button
          className="icon-button project-header-bar__sort-roles"
          onClick={() => {
            void onSortRoles();
          }}
          disabled={sortRolesDisabled}
          type="button"
          title="Sort roles by type"
          aria-label="Sort roles by type"
        >
          <ArrowDownAZ size={16} />
        </button>
      )}
    </div>
  );
}

export interface ProjectSummaryColumnProps {
  project: ProjectSummary;
  formData: ProjectSummaryColumnFormData;
  setFormData: React.Dispatch<
    React.SetStateAction<ProjectSummaryColumnFormData>
  >;
  editingField: string | null;
  setEditingField: (field: string | null) => void;
  onSave: (field: string) => Promise<void>;
  onKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
  ) => void;
  validationError: string | null;
  saving: boolean;
  onClearValidationError?: () => void;
  /** Lock Deal Stage, LtC, and Start Date fields (non-editable) */
  locked?: boolean;
  /**
   * Opens the "Clear all assignments" confirmation. Omit to hide the control.
   * Only offered in the expanded view; canClearAllAssignments decides whether
   * it renders.
   */
  onClearAllAssignments?: () => void;
  /** Header bar props consumed by the combined header rendered above the body. */
  headerRowProps: ProjectMetaHeaderRowProps;
}

/**
 * Project summary column for the expanded project view:
 * deal stage, likelihood to close, start and end dates.
 */
export function ProjectSummaryColumn({
  project,
  formData,
  setFormData,
  editingField,
  setEditingField,
  onSave,
  onKeyDown,
  validationError,
  saving,
  onClearValidationError,
  locked,
  onClearAllAssignments,
}: ProjectSummaryColumnProps) {
  const { dealStages } = useDealStages();
  return (
    <>
      <div className="project-meta-cell__row-group">
        <div className="project-meta-cell__row">
          <span className="project-meta-cell__label">Deal Stage</span>
          {editingField === "deal_stage" ? (
            <select
              className="project-meta-cell__input"
              value={formData.deal_stage_id}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  deal_stage_id: e.target.value,
                }))
              }
              onBlur={() => onSave("deal_stage")}
              onKeyDown={(e) => onKeyDown(e, "deal_stage")}
              autoFocus
              disabled={saving}
            >
              {dealStages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          ) : (
            <span
              className={`project-meta-cell__value${locked ? "" : " project-card__editable"}`}
              onClick={locked ? undefined : () => setEditingField("deal_stage")}
            >
              {project.deal_stage?.name}
            </span>
          )}
        </div>
        <div className="project-meta-cell__row">
          <span className="project-meta-cell__label">LtC</span>
          {editingField === "likelihood_to_close" ? (
            <input
              type="number"
              className="project-meta-cell__input"
              style={{ width: "4rem", minWidth: "4rem" }}
              value={formData.likelihood_to_close}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  likelihood_to_close: e.target.value,
                }))
              }
              onBlur={() => onSave("likelihood_to_close")}
              onKeyDown={(e) => onKeyDown(e, "likelihood_to_close")}
              placeholder="0-100"
              min="0"
              max="100"
              step="1"
              autoFocus
              disabled={saving}
            />
          ) : (
            <span
              className={`project-meta-cell__value${locked ? "" : " project-card__editable"}`}
              onClick={
                locked
                  ? undefined
                  : () => setEditingField("likelihood_to_close")
              }
            >
              {formatLikelihoodToClose(project.likelihood_to_close)}
            </span>
          )}
        </div>
      </div>
      <div className="project-meta-cell__row-group">
        <div className="project-meta-cell__row">
          <span className="project-meta-cell__label">Start</span>
          {editingField === "start_date" ? (
            <MonDateInput
              minDate={currentWeekStartISO()}
              value={formData.start_date}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, start_date: value }))
              }
              onCommit={() => onSave("start_date")}
              disabled={saving}
              fontSize="0.75rem"
              className="project-meta-cell__week-stepper"
              autoFocus
            />
          ) : (
            <span
              className={`project-meta-cell__value${locked ? "" : " project-card__editable"}`}
              onClick={locked ? undefined : () => setEditingField("start_date")}
            >
              {formatUTCDateMDY(project.start_date)}
            </span>
          )}
        </div>
        <div className="project-meta-cell__row">
          <span className="project-meta-cell__label">End</span>
          {editingField === "num_weeks" ? (
            <MonDateInput
              value={
                formData.start_date && formData.num_weeks
                  ? computeProjectEndDate(
                      formData.start_date,
                      formData.num_weeks,
                    )
                  : undefined
              }
              minDate={
                formData.start_date
                  ? computeProjectEndDate(formData.start_date, 2)
                  : undefined
              }
              onChange={(value) => {
                const weeks = computeNumWeeks(formData.start_date, value);
                setFormData((prev) => ({ ...prev, num_weeks: weeks }));
                onClearValidationError?.();
              }}
              onCommit={() => onSave("num_weeks")}
              disabled={saving}
              fontSize="0.75rem"
              className="project-meta-cell__week-stepper"
              autoFocus
            />
          ) : (
            <span
              className="project-meta-cell__value project-card__editable"
              onClick={() => setEditingField("num_weeks")}
            >
              {project.start_date && project.num_weeks != null
                ? formatUTCDateMDY(
                    computeProjectEndDate(
                      project.start_date,
                      project.num_weeks,
                    ),
                  )
                : "—"}
            </span>
          )}
        </div>
      </div>
      {onClearAllAssignments && canClearAllAssignments(project) && (
        <button
          type="button"
          className="project-card__meta-action project-card__editable project-meta-cell__clear-all"
          data-testid="clear-all-assignments-button"
          onClick={onClearAllAssignments}
          disabled={saving}
          title="Remove every assignee from every role on this project"
        >
          Clear all assignments
        </button>
      )}
    </>
  );
}
